import { useMemo, useState } from 'react';

import { estateBucketFor } from '@/lib/estate-buckets';

interface EstateGraph {
  nodes: Array<{ id: string; kind: string; label: string; sourceType?: string | null }>;
  edges: Array<{ from: string; to: string; severity: string }>;
}

interface Row {
  key: string;
  title: string;
  items: Array<{ id: string; label: string; sev: string; n: number }>;
}

export function EstateMap({
  graph,
  onPick,
  mode = 'map',
}: {
  graph: EstateGraph | null;
  /**
   * 'table' renders the same data as a sortable, searchable list.
   *
   * Not a fallback. For "where exactly does this asset live", a sorted table
   * with a filter beats any picture - you can read an exact name, sort by
   * severity, and search. The map earns its place only where spatial grouping
   * says something a list cannot, so both ship and the operator picks.
   */
  mode?: 'map' | 'table';
  /**
   * Fired when a card is clicked, with the asset's node id.
   *
   * The ids here ARE the connection-graph's node ids (`asset:<uuid>`), so the
   * caller can drive the graph straight to it - the estate map answers "where
   * does this live" and the graph answers "what does it touch", and clicking
   * through is what joins the two questions.
   */
  onPick?: (assetId: string) => void;
}) {
  const [focus, setFocus] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sevFilter, setSevFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [sortBy, setSortBy] = useState<'sev' | 'name' | 'where'>('sev');

  const rows = useMemo(() => {
    const titles: Array<[string, string]> = [
      ['cloud', 'Cloud storage'],
      ['database', 'Databases'],
      ['fileserver', 'File servers'],
      ['saas', 'SaaS'],
      ['endpoint', 'Endpoints'],
      ['mainframe', 'Mainframe / IBM i'],
      ['other', 'Other'],
    ];
    const out: Row[] = titles.map(([key, title]) => ({ key, title, items: [] }));
    if (!graph) return out;

    const rank = (s: string) => (s === 'critical' ? 4 : s === 'high' ? 3 : s === 'medium' ? 2 : 1);
    const sev: Record<string, string> = {};
    const num: Record<string, number> = {};
    for (const e of graph.edges) {
      const s = e.severity === 'critical' || e.severity === 'high' || e.severity === 'medium' ? e.severity : 'low';
      if (!sev[e.from] || rank(s) > rank(sev[e.from]!)) sev[e.from] = s;
      num[e.from] = (num[e.from] ?? 0) + 1;
    }

    for (const n of graph.nodes) {
      if (n.kind !== 'asset') continue;
      const key = estateBucketFor(n.sourceType ?? undefined, n.label);
      const row = out.filter((r) => r.key === key)[0];
      if (row) row.items.push({ id: n.id, label: n.label, sev: sev[n.id] ?? 'low', n: num[n.id] ?? 0 });
    }
    for (const r of out) {
      r.items.sort((a, b) => rank(b.sev) - rank(a.sev) || a.label.localeCompare(b.label));
    }
    return out;
  }, [graph]);

  if (!graph) {
    return <div className="h-64 grid place-items-center text-sm text-muted-foreground">Loading estate...</div>;
  }

  const live = rows.filter((r) => r.items.length > 0);

  if (mode === 'table') {
    const all: Array<{ id: string; label: string; sev: string; n: number; where: string }> = [];
    for (const r of live) {
      for (const it of r.items) all.push({ ...it, where: r.title });
    }
    const q = query.trim().toLowerCase();
    const filtered = all.filter((a) => {
      if (sevFilter !== 'all' && a.sev !== sevFilter) return false;
      if (!q) return true;
      return a.label.toLowerCase().indexOf(q) >= 0 || a.where.toLowerCase().indexOf(q) >= 0;
    });
    const rank = (x: string) => (x === 'critical' ? 4 : x === 'high' ? 3 : x === 'medium' ? 2 : 1);
    filtered.sort((a, b) =>
      sortBy === 'name'
        ? a.label.localeCompare(b.label)
        : sortBy === 'where'
          ? a.where.localeCompare(b.where) || rank(b.sev) - rank(a.sev)
          : rank(b.sev) - rank(a.sev) || b.n - a.n || a.label.localeCompare(b.label),
    );

    return (
      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search asset or location..."
            className="px-3 py-1.5 rounded-md border border-border/60 bg-background text-xs w-64"
          />
          {(['all', 'critical', 'high', 'medium', 'low'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setSevFilter(f)}
              className={`px-2.5 py-1 rounded-md text-[11px] capitalize border ${
                sevFilter === f ? 'border-primary/50 bg-primary/10' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto text-[11px] font-mono text-muted-foreground">
            {filtered.length} of {all.length}
          </span>
        </div>

        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="max-h-[460px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  {([['name', 'Asset'], ['where', 'Where it lives'], ['sev', 'Severity']] as const).map(
                    ([key, title]) => (
                      <th key={key} className="px-3 py-2 font-medium">
                        <button
                          type="button"
                          onClick={() => setSortBy(key)}
                          className={sortBy === key ? 'text-foreground' : 'hover:text-foreground'}
                        >
                          {title}
                          {sortBy === key ? ' ↓' : ''}
                        </button>
                      </th>
                    ),
                  )}
                  <th className="px-3 py-2 font-medium text-right">Findings</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => {
                      setPicked(a.id);
                      if (onPick) onPick(a.id);
                    }}
                    className="border-t border-border/40 hover:bg-foreground/[0.04] cursor-pointer"
                  >
                    <td className="px-3 py-2 font-mono truncate max-w-[380px]">
                      <span
                        className="inline-block w-1 h-3 mr-2 align-middle rounded-sm"
                        style={{
                          background:
                            a.sev === 'critical' ? '#E2484F'
                            : a.sev === 'high' ? '#E7A13C'
                            : a.sev === 'medium' ? '#8A94A6'
                            : '#98A2B3',
                        }}
                      />
                      {a.label}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{a.where}</td>
                    <td className="px-3 py-2 capitalize">{a.sev}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{a.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Click a row to focus that asset in the connection graph below.
        </p>
      </div>
    );
  }

  const shown = focus ? live.filter((r) => r.key === focus) : live;
  const total = live.reduce((s, r) => s + r.items.length, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold">Data estate</div>
          <p className="text-xs text-muted-foreground">
            Every card is an asset, placed where it physically lives. Column width is asset count.
          </p>
        </div>
        <div className="text-xs font-mono text-muted-foreground">
          {total} assets in {live.length} sources
          {focus ? (
            <button type="button" onClick={() => setFocus(null)} className="ml-3 text-primary hover:underline">
              back to all
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex gap-2" style={{ height: 440 }}>
        {shown.map((r) => {
          const crit = r.items.filter((i) => i.sev === 'critical').length;
          return (
            <section
              key={r.key}
              className="rounded-lg border border-border/60 bg-card flex flex-col overflow-hidden transition-all duration-500"
              style={{ flexGrow: focus ? 1 : r.items.length, flexBasis: 0, minWidth: 140 }}
            >
              <button
                type="button"
                onClick={() => setFocus(focus ? null : r.key)}
                className="px-3 py-2 text-left border-b border-border/60 hover:bg-foreground/5"
              >
                <div className="text-[11px] font-medium truncate">{r.title}</div>
                <div className="text-[10px] font-mono text-muted-foreground">
                  {r.items.length} assets
                  {crit > 0 ? <span className="text-destructive"> - {crit} critical</span> : null}
                </div>
              </button>
              <div className="flex-1 overflow-y-auto p-2">
                <div className="flex flex-wrap gap-1 content-start">
                  {r.items.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      title={a.label + ' - ' + a.sev + ', ' + a.n + ' findings'}
                      onClick={() => {
                        const next = picked === a.id ? null : a.id;
                        setPicked(next);
                        if (next && onPick) onPick(next);
                      }}
                      className="rounded border border-border/60 bg-muted/30 hover:-translate-y-0.5 transition-transform"
                      style={{
                        borderLeftWidth: a.sev === 'critical' || a.sev === 'high' ? 3 : a.sev === 'medium' ? 2 : 1,
                        borderLeftColor:
                          picked === a.id ? '#F15D27'
                          : a.sev === 'critical' ? '#E2484F'
                          : a.sev === 'high' ? '#E7A13C'
                          : a.sev === 'medium' ? '#8A94A6'
                          : '#98A2B3',
                        padding: focus ? '5px 8px' : '4px 6px',
                        maxWidth: focus ? 240 : 150,
                      }}
                    >
                      <div className="font-mono truncate" style={{ fontSize: focus ? 11 : 10, maxWidth: focus ? 224 : 138 }}>
                        {a.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
